import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuChevronDown, LuFileSpreadsheet, LuFolderKanban, LuSearch, LuX } from 'react-icons/lu';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { GLASS_FIELD } from '../../components/ui/glass';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useTranslation } from '../../i18n/LanguageContext';
import useEmployeeProjects from './useEmployeeProjects';
import { downloadEmployeeProjectsReport } from './projectsReport';
import ProjectsList from './ProjectsList';
import ProjectDetailModal from './ProjectDetailModal';
// Only global variables are left in this file, but legacy modals depend on
// them; importing it here keeps its place in the cascade unchanged.
import './ProjectsModal.module.css';

const selectClass = `${GLASS_FIELD} tw-cursor-pointer tw-appearance-none tw-pr-9 [&>option]:tw-bg-white [&>option]:tw-text-slate-800 dark:[&>option]:tw-bg-[#1f232b] dark:[&>option]:tw-text-slate-100`;

const FilterSelect = ({ label, value, onChange, options }) => (
  <label className="tw-flex tw-min-w-[8.5rem] tw-flex-1 tw-flex-col tw-gap-1 sm:tw-flex-none">
    <span className="tw-px-1 tw-text-xs tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{label}</span>
    <span className="tw-relative tw-block">
      <select value={value} onChange={(event) => onChange(event.target.value)} className={selectClass}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <LuChevronDown aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
    </span>
  </label>
);

FilterSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
};

/** "Projeler": an employee's projects, opened from the dashboard and Çalışan Yönetimi. */
const ProjectsModal = ({ employee, onClose }) => {
  const { t, language } = useTranslation();
  const { userAuthorization } = useUserProfile();
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');
  const projects = useEmployeeProjects(employee);
  const [detailProject, setDetailProject] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (projects.filteredRows.length === 0) {
      toast.info(t('projects.toast.nothingToExport'));
      return;
    }
    setExporting(true);
    try {
      await downloadEmployeeProjectsReport({ rows: projects.filteredRows, employee, isAdmin });
      toast.success(t('projects.toast.exportSuccess'));
    } catch (error) {
      console.error('Error exporting projects report:', error);
      toast.error(t('projects.toast.exportError'));
    } finally {
      setExporting(false);
    }
  };

  let emptyMessage = t('projects.emptyFiltered');
  if (projects.loadFailed) emptyMessage = t('projects.toast.loadError');
  else if (projects.rows.length === 0) emptyMessage = projects.showAssignedOnly ? t('projects.emptyAssigned') : t('projects.emptyAll');

  const statusOptions = [
    { value: 'all', label: t('common.all') },
    { value: 'active', label: t('common.active') },
    { value: 'passive', label: t('common.passive') },
  ];

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="xl"
        lang={language}
        title={t('projects.title')}
        subtitle={`${employee.eName || ''} ${employee.eSurname || ''}`.trim()}
        icon={(
          <GlassIcon gradient="tw-from-violet-500 tw-to-fuchsia-500 tw-shadow-violet-500/30">
            <LuFolderKanban aria-hidden="true" className="tw-h-6 tw-w-6" />
          </GlassIcon>
        )}
        actions={(
          <>
            <SegmentedControl
              size="sm"
              layoutId="projects-scope-pill"
              label={t('projects.title')}
              value={projects.showAssignedOnly ? 'assigned' : 'all'}
              onChange={(value) => projects.setShowAssignedOnly(value === 'assigned')}
              options={[
                { value: 'assigned', label: t('projects.assignedOnly'), title: t('projects.showAssigned') },
                { value: 'all', label: t('projects.allProjects'), title: t('projects.showAll') },
              ]}
            />
            <Button variant="success" size="sm" onClick={handleExport} disabled={exporting || projects.loading}>
              <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4" />
              {exporting ? t('projects.exporting') : t('projects.export')}
            </Button>
          </>
        )}
        closeLabel={t('common.close')}
        bodyClassName="tw-p-0"
      >
        <div className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-border-b tw-border-slate-200/80 tw-bg-white/95 tw-px-4 tw-py-4 sm:tw-px-6 dark:tw-border-white/10 dark:tw-bg-[#1f232b]/95">
          <div className="tw-relative tw-min-w-0 tw-basis-full lg:tw-flex-1 lg:tw-basis-auto">
            <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
            <input
              type="text"
              value={projects.searchTerm}
              onChange={(event) => projects.setSearchTerm(event.target.value)}
              placeholder={t('projects.searchPlaceholder')}
              className={`${GLASS_FIELD} tw-pl-10 tw-pr-10`}
            />
            {projects.searchTerm && (
              <button
                type="button"
                onClick={() => projects.setSearchTerm('')}
                aria-label={t('common.clearSearch')}
                className="tw-absolute tw-right-2 tw-top-1/2 tw-grid tw-h-7 tw-w-7 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.05] hover:tw-text-slate-700 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white"
              >
                <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
              </button>
            )}
          </div>
          <FilterSelect
            label={t('projects.assignmentStatus')}
            value={projects.assignmentStatusFilter}
            onChange={projects.setAssignmentStatusFilter}
            options={statusOptions}
          />
          {/* Project status values are stored in Turkish on the documents */}
          <FilterSelect
            label={t('projects.projectStatus')}
            value={projects.projectStatusFilter}
            onChange={projects.setProjectStatusFilter}
            options={[
              { value: 'all', label: t('common.all') },
              { value: 'Aktif', label: t('common.active') },
              { value: 'Pasif', label: t('common.passive') },
            ]}
          />
          <span className="tw-pb-2.5 tw-text-sm tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">
            {t('projects.count', { count: projects.filteredRows.length })}
          </span>
        </div>

        <div className="tw-px-4 tw-py-4 sm:tw-px-6 sm:tw-py-5">
          <ProjectsList
            rows={projects.filteredRows}
            loading={projects.loading}
            emptyMessage={emptyMessage}
            isAdmin={isAdmin}
            onOpenDetail={setDetailProject}
          />
        </div>
      </Modal>

      {detailProject && (
        <ProjectDetailModal
          project={detailProject.project}
          assignments={detailProject.assignments}
          employee={employee}
          isAdmin={isAdmin}
          onClose={() => setDetailProject(null)}
        />
      )}
    </>
  );
};

ProjectsModal.propTypes = {
  employee: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProjectsModal;
